import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {useEffect, useRef, useState} from 'react';

import {usePrefersReducedMotion} from '@udemy/design-system-utils';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {HeadSEO} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {
    CertificationSecondChanceBanner,
    CertificationExamCarousel,
    VoucherUIRegion,
    type CertificationBundle,
} from '@udemy/react-certification-preparation';
import {useCarouselExams, VoucherPoolProductType} from '@udemy/react-certification-vouchers';
import {ContentArea} from '@udemy/react-layout-components';

import {PersonalPlanBanner} from 'src/components/personal-plan-banner';
import {CourseProvider} from 'src/hooks/certification_vouchers/useCourses';
import {useCanonicalUrl} from 'src/hooks/useCanonicalUrl';
import {useIsMobileAppView} from 'src/hooks/useIsMobileAppView';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {PageProps} from 'src/types/page-props';

import {FrequentlyAskedQuestions} from './components/all-vouchers-faq/all-vouchers-faq.react-component';
import {getCertificationVouchersFAQ} from './components/all-vouchers-faq/faq-data';
import {Banner} from './components/banner/banner.react-component';
import {CourseUnitsWithProviders} from './components/course-units';
import styles from './index.module.less';

export type AllCertificationVouchersLandingPageFeatureProps = PageProps & {
    certificationBundle?: CertificationBundle | null;
};

const SECOND_CHANCE_BANNER_ANCHOR_ID = 'second-chance-banner';

export const AllCertificationVouchersLandingPageFeature: React.FC<
    AllCertificationVouchersLandingPageFeatureProps
> = (props) => {
    const secondChanceCarouselRef = useRef<HTMLDivElement>(null);
    const secondChanceBannerRef = useRef<HTMLDivElement>(null);
    const hasScrolledToSecondChanceRef = useRef(false);
    const [currentProvider, setCurrentProvider] = useState<CourseProvider>('all');
    const {gettext} = useI18n();
    const router = useRouter();
    const prefersReducedMotion = usePrefersReducedMotion();
    const {url: canonicalUrl} = useCanonicalUrl([], META_DEFAULT_BASE_URL);
    const {isReady, isMobileApp} = useIsMobileAppView();
    // Fail-safe: until we know for sure this isn't the native app's webview, treat it as if it
    // were, so restricted content never renders and then disappears once we find out.
    const hideForMobileApp = !isReady || isMobileApp;
    // Match the fragment specifically rather than testing the whole path: a substring check would
    // also fire on e.g. `?utm_content=second-chance-banner` or any future route containing it.
    const hasSecondChanceRedirect =
        router?.asPath?.split('#')[1] === SECOND_CHANCE_BANNER_ANCHOR_ID;
    const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    const handleProviderChange = (providerId: string) => {
        setCurrentProvider(providerId as CourseProvider);
    };

    const metaProps = {
        title: gettext('Udemy Cert Prep + Exam Vouchers'),
        description: gettext(
            'Turn prep into success with Udemy. Get expert-led training and save at least 10% when booking your certification exam voucher.',
        ),
    };

    const certificationVouchersFAQ = getCertificationVouchersFAQ(gettext);

    const {exams: udemyIssuedRetakeExams, isLoading: isUdemyIssuedRetakeLoading} = useCarouselExams(
        1,
        [VoucherPoolProductType.UdemyIssuedRetakeInsurance],
    );
    const hasUdemyIssuedRetakeExams =
        !isUdemyIssuedRetakeLoading && udemyIssuedRetakeExams.length > 0;

    useEffect(() => {
        const banner = secondChanceBannerRef.current;

        if (
            !isReady ||
            !hasSecondChanceRedirect ||
            !hasUdemyIssuedRetakeExams ||
            hasScrolledToSecondChanceRef.current ||
            !banner
        ) {
            return;
        }

        banner.scrollIntoView({behavior: scrollBehavior, block: 'start'});
        // Moving the viewport alone leaves keyboard and screen-reader users at the top of the
        // page, so send focus to the section too. The scroll above already positioned it, hence
        // `preventScroll`.
        banner.focus({preventScroll: true});
        hasScrolledToSecondChanceRef.current = true;
    }, [isReady, hasSecondChanceRedirect, hasUdemyIssuedRetakeExams, scrollBehavior]);

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.title}
                description={metaProps.description}
                ogDescription={metaProps.description}
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[props.locale]}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <FunnelLogContextProvider pageType="allVouchers">
                <>
                    <Banner />
                    <ContentArea className="ud-container">
                        <div className={styles['carousel-container']}>
                            <CertificationExamCarousel
                                uiRegion={'voucher_landing' as VoucherUIRegion}
                                title={gettext('Get your exam vouchers and save')}
                                titleClassName="ud-text-bold"
                                onProviderChange={handleProviderChange}
                                poolProductTypes={[
                                    VoucherPoolProductType.Default,
                                    VoucherPoolProductType.RetakeInsurance,
                                ]}
                                ctaMode={hideForMobileApp ? 'buyNow' : 'addToCart'}
                            />

                            {hasUdemyIssuedRetakeExams && (
                                <div
                                    id={SECOND_CHANCE_BANNER_ANCHOR_ID}
                                    ref={secondChanceBannerRef}
                                    // Focusable only programmatically, so the deep-link scroll can
                                    // also move focus here without adding a tab stop.
                                    tabIndex={-1}
                                    className={styles['scroll-target']}
                                >
                                    <CertificationSecondChanceBanner
                                        onBuyButtonClick={() => {
                                            secondChanceCarouselRef.current?.scrollIntoView({
                                                behavior: scrollBehavior,
                                                block: 'start',
                                            });
                                        }}
                                    />
                                </div>
                            )}
                            <div className={styles['scroll-target']} ref={secondChanceCarouselRef}>
                                <CertificationExamCarousel
                                    uiRegion={'voucher_landing' as VoucherUIRegion}
                                    title={gettext(
                                        'Get your exam vouchers with a guaranteed safety net',
                                    )}
                                    titleClassName="ud-text-bold"
                                    onProviderChange={handleProviderChange}
                                    poolProductTypes={[
                                        VoucherPoolProductType.UdemyIssuedRetakeInsurance,
                                    ]}
                                    ctaMode={hideForMobileApp ? 'buyNow' : 'addToCart'}
                                />
                            </div>
                        </div>

                        <CourseUnitsWithProviders
                            provider={currentProvider}
                            certificationBundle={props.certificationBundle}
                        />

                        {!hideForMobileApp && <PersonalPlanBanner />}

                        <div>
                            <FrequentlyAskedQuestions
                                questionsAndAnswers={certificationVouchersFAQ}
                            />
                        </div>
                    </ContentArea>
                </>
            </FunnelLogContextProvider>
        </>
    );
};
